"use client";

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

/** Fixed-position coordinates for a portal-rendered menu, relative to the viewport. */
export interface FloatingMenuPosition {
  top: number;
  left: number;
  width: number;
  /** Whether the menu opens above the trigger instead of below (not enough space below). */
  opensUpward: boolean;
}

interface UseFloatingMenuPositionOptions {
  /** Whether the menu is currently open — position is only tracked while true. */
  isOpen: boolean;
  /** Max menu height (px), used to decide whether it should open upward instead of downward. */
  maxHeight: number;
  /** Menu is at least the trigger's width, wider when this is set (e.g. a compact trigger with a
   *  wide option list). Default 0. */
  minWidth?: number;
  /** Margin (px) kept from the viewport edge when clamping horizontal position. Default 8. */
  viewportMargin?: number;
}

/**
 * Tracks a floating (portal-rendered) menu's fixed-position coordinates relative to its trigger
 * element, recalculating on open, scroll, and resize. Shared by every `createPortal`-based
 * dropdown/popover in this codebase (`FetchSearchableDropdown`, `RoomTypeVisualMultiSelect`) so
 * the viewport-clamping and upward-opening logic can't drift or get re-copied into a third place.
 *
 * Scroll recalculation is coalesced to at most once per animation frame via `requestAnimationFrame`
 * — the naive per-scroll-event version reads layout (`getBoundingClientRect`) and re-renders on
 * every scroll tick, competing for frame budget with anything else animating on the page (e.g. a
 * wizard step's own transition).
 *
 * @param triggerRef - Ref to the element the menu is anchored to.
 * @param options - Open state + sizing options.
 * @returns The menu's current position, and a `recalculate` callback for callers that need to
 *   force a refresh outside the tracked open/scroll/resize events.
 */
export function useFloatingMenuPosition(
  triggerRef: RefObject<HTMLElement | null>,
  { isOpen, maxHeight, minWidth = 0, viewportMargin = 8 }: UseFloatingMenuPositionOptions
): { position: FloatingMenuPosition; recalculate: () => void } {
  const [position, setPosition] = useState<FloatingMenuPosition>({ top: 0, left: 0, width: 0, opensUpward: false });
  const rafRef = useRef<number | null>(null);

  const recalculate = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const GAP = 4;
    const spaceBelow = window.innerHeight - rect.bottom;
    const opensUpward = spaceBelow < maxHeight + GAP;
    const width = Math.max(rect.width, minWidth);
    const left = Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - width - viewportMargin));
    setPosition({
      top: opensUpward ? rect.top - GAP : rect.bottom + GAP,
      left,
      width,
      opensUpward,
    });
  }, [triggerRef, maxHeight, minWidth, viewportMargin]);

  useEffect(() => {
    if (isOpen) recalculate();
  }, [isOpen, recalculate]);

  useEffect(() => {
    if (!isOpen) return;
    const scheduleRecalculate = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        recalculate();
      });
    };
    window.addEventListener("scroll", scheduleRecalculate, true);
    window.addEventListener("resize", scheduleRecalculate);
    return () => {
      window.removeEventListener("scroll", scheduleRecalculate, true);
      window.removeEventListener("resize", scheduleRecalculate);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, recalculate]);

  return { position, recalculate };
}
